import styled, { css } from 'styled-components';
import { layout, media } from '@prisjakt/ui-base';

import {
  evenColumnBackgroundColor,
  oddColumnBackgroundColor,
  compareColors,
  dummyMaxWidth,
  mobileItemMaxWidth,
  tabletItemMaxWidth,
  desktopItemMaxWidth,
  itemFlex,
  oddColumnHighlightColor,
  evenColumnHighlightColor,
  highlightNewEvenColumnAnimation,
  highlightNewOddColumnAnimation,
} from '../../../../constants';

const HEADER_HEIGHT = 47;

const dummyStyling = css`
  background-color: ${evenColumnBackgroundColor};

  ${media.smallOnly`
    max-width: 100%;
  `};
`;

const sharedPadding = css`
  padding-left: ${layout.measurements.spacing100};
  padding-right: ${layout.measurements.spacing100};
`;

const sharedSectionHeaderStyles = css`
  display: flex;
  width: 100%;
  height: ${HEADER_HEIGHT}px;
  margin: 0;
  left: 0;
  top: initial;
  position: relative;
`;

export const TitleWrapper = styled.div`
  display: flex;
  flex: 0;
  align-items: center;
  left: 0;
  text-align: left;
  font-weight: 600;
  z-index: 2;
  position: absolute;

  @supports (position: sticky) {
    position: sticky;
  }
`;

type SectionHeaderProps = {
  open?: boolean;
  desktopScaleToFit?: boolean;
  shouldAnimate?: boolean;
};

export const SectionHeader = styled.div<SectionHeaderProps>(
  props => css`
    ${sharedSectionHeaderStyles};
    color: ${props.theme.text.linkExternal};
    cursor: pointer;
    flex: 1 1 0%;

    &:active {
      color: ${props.theme.colors.base700};
    }

    /** Section headers are offset by 1, so the inverse styles for even/odd columns apply */
    &:nth-child(odd) {
      background-color: ${evenColumnBackgroundColor};
      ${props.shouldAnimate && highlightNewEvenColumnAnimation}
    }

    &:nth-child(even) {
      background-color: ${oddColumnBackgroundColor};
      ${props.shouldAnimate && highlightNewOddColumnAnimation}
    }

    ${!props.open &&
      css`
        &:after {
          content: '';
          display: block;
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 1px;
          background-color: ${props.theme.colors.base0};
        }
      `};

    ${media.smallOnly`
      max-width: ${mobileItemMaxWidth};
    `}

    ${media.mediumOnly`
      width: ${tabletItemMaxWidth};
    `}

    ${media.largeUp`
      width: ${props.desktopScaleToFit && desktopItemMaxWidth};
    `}
  `
);

export const Title = styled.span(
  props => css`
    position: absolute;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-left: ${layout.measurements.spacing050};
    color: ${props.theme.text.callToAction};
    cursor: pointer;

    > svg {
      vertical-align: middle;
      margin-right: 0.5rem;
    }
  `
);

export const PropertyHeader = styled.div`
  flex: 0;
  text-align: left;
  position: absolute;
  left: 0;

  @supports (position: sticky) {
    position: sticky;
  }
`;

type PropertyListProps = {
  isDummy?: boolean;
  hasMaxWidth?: boolean;
};

export const PropertyList = styled.div<PropertyListProps>`
  border-bottom: 1px solid ${p => p.theme.lines.secondary};
  display: flex;
  flex: ${itemFlex};

  ${PropertyHeader} {
    flex: 0;
  }

  ${p =>
    p.isDummy &&
    css`
      ${dummyStyling};

      ${p.hasMaxWidth &&
        css`
          max-width: ${dummyMaxWidth}px;
        `};
    `};
`;

type PropertyProps = {
  isDiff?: boolean | null;
  desktopScaleToFit?: boolean;
  shouldAnimate?: boolean;
};

export const Property = styled.div<PropertyProps>(
  ({ isDiff, shouldAnimate, desktopScaleToFit }) => css`
    ${sharedPadding};
    text-overflow: ellipsis;
    overflow: hidden;
    margin: 0;
    padding-top: ${layout.measurements.spacing250};
    padding-bottom: ${layout.measurements.spacing050};
    flex: 1 1 0%;

    /** Property cells are offset by 1, so the inverse styles for even/odd columns apply */
    &:nth-child(odd) {
      background-color: ${
        isDiff ? evenColumnHighlightColor : evenColumnBackgroundColor
      };
      ${shouldAnimate && !isDiff && highlightNewEvenColumnAnimation}
    }

    &:nth-child(even) {
      background-color: ${
        isDiff ? oddColumnHighlightColor : oddColumnBackgroundColor
      };
      ${shouldAnimate && !isDiff && highlightNewOddColumnAnimation}
    }

    ${media.smallOnly`
      max-width: ${mobileItemMaxWidth};
    `}

    ${media.mediumOnly`
      width: ${tabletItemMaxWidth};
    `}

    ${media.largeUp`
      width: ${desktopScaleToFit && desktopItemMaxWidth};
    `}
  `
);

type DummySectionHeaderProps = {};

export const DummySectionHeader = styled.div<DummySectionHeaderProps>`
  ${dummyStyling};
  ${sharedSectionHeaderStyles};
  flex: 1 1 0%;
`;

type SectionProps = {
  open: boolean;
  hasMaxWidth?: boolean;
};

export const Section = styled.div<SectionProps>`
  display: flex;

  ${p =>
    p.hasMaxWidth &&
    css`
      max-width: ${dummyMaxWidth}px;
    `};

  > * {
    border-bottom: 1px solid ${p => p.theme.lines.secondary};

    ${p =>
      p.open &&
      css`
        border-bottom: 2px solid ${compareColors.separator};
      `}
  }

  &:hover {
    ${Title} {
      color: ${p => p.theme.text.callToActionHover};
    }
  }
`;

type PropertyHeaderLabelProps = {
  depth: number;
};

export const PropertyHeaderLabel = styled.span<PropertyHeaderLabelProps>`
  position: absolute;
  top: 0;
  left: 0;
  ${sharedPadding};
  padding-top: ${layout.measurements.spacing050};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${p => p.theme.text.quaternary};

  ${media.mediumDown`
    background-color: transparent;
    padding: 0;
    margin: 5px ${layout.measurements.spacing050};
    text-transform: none;
  `}
`;

export const SectionHeaderButton = styled.button``;
